import React, { useEffect, useReducer, useState } from 'react';
import PageContainer from '../../components/PageContainer';
import MainContent from '../../components/MainContent';
import PageTitle from '../../components/Common/PageTitle/PageTitle';
import TextInput from '../../components/Common/TextInput';
import Notification from '../../components/Common/Notification';
import './EnterMarksPage.css';
import { useConfig } from '../../DataStore/ConfigContext';
import { useMarks } from '../../DataStore/MarksContext';
import ActivityLoader from '../../components/Common/ActivityLoader';
import { useTheme } from '../../DataStore/ThemeContext';
import ajaxMark from '../../util/remote/ajaxMark';
import AlertDeleteMark from '../../components/Academics/AlertDeleteMark';
import ActionDropdownItem from '../../components/Common/PageTitle/ActionDropdownItem';
import AlertOrganiseClass from '../../components/Academics/AlertOrganiseClass';
import AlertUnorganiseClass from '../../components/Academics/AlertUnorganiseClass';
import AlertRecallAdd from '../../components/Academics/AlertRecallAdd';
import AlertCopyMarks from '../../components/Academics/MarksEntry/AlertCopyMarks';

const initialState = {
  class: null,
  stream: null,
  subject: null,
  marks: {},
  loaded: false,
  showFilter: true,
  searchQuery: '',
};

function reducer(state, action) {
  switch (action.type) {
    case 'TOGGLE_FILTER':
      return { ...state, showFilter: !state.showFilter };
    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.value };
    case 'HIDE_FILTER':
      return { ...state, showFilter: false };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

const OrganiseMarksPage = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [warnDelete, setWarnDelete] = useState(false);
  const { classes, getClasses, currentAcademicPeriod, getCurrentAcademicPeriod } = useConfig();
  const [studentProgress, setStudentProgess] = useState(false);
  const { setNotify, openSettings } = useTheme();
  const [showOrganiseClass, setShowOrganiseClass] = useState(false);
  const [showUnorganiseClass, setShowUnorganiseClass] = useState(false);
  const [showRecallAdd, setShowRecallAdd] = useState(false);

  const {
    getSubjectPapers,
    paperListing,
    setPaper,
    setClass,
    setStream,
    paper,
    classRoom,
    stream,
    organisedMarks,
    getOrganisedMarks,
    loadingOrganizer,
  } = useMarks();

  useEffect(() => {
    getClasses();
    getSubjectPapers();
    getCurrentAcademicPeriod();
  }, []);


  useEffect(() => {
    if (Array.isArray(organisedMarks)) {
      dispatch({ type: 'HIDE_FILTER' });
    }
  }, [organisedMarks]);

  const classOptions = Array.isArray(classes)
    ? classes.map(cls => ({
        label: `${cls.class_name} (${cls.level?.level || ''})`,
        value: cls,
        streams: cls.streams || [],
      }))
    : [];

  const streamOptions = classRoom?.streams?classRoom.streams.map(s => ({ label: s.stream_name, value: s.stream_id })) : [];

  const subjectOptions = Array.isArray(paperListing) && classRoom?.level?.level_id
    ? paperListing
        .filter(paper => paper.subject.level.level_id === classRoom.level.level_id)
        .map(paper => ({ label: `${paper.subject.subject} (${paper.paper_code})`, value: paper.paper_id }))
    : [];

  const handleLoadStudents = () => {
    if (!classRoom || !paper) return;
    getOrganisedMarks();
  };

  const classLabel = classRoom?.class_name || '';
  const streamLabel = stream?.label || '';
  const subjectLabel = paper?.label || '';

  const filteredStudents = Array.isArray(organisedMarks)
    ? organisedMarks.filter(record => {
        const q = (state.searchQuery || '').toLowerCase();
        return (
          (record.student.reg_no?.toLowerCase() || '').includes(q) ||
          (record.student.full_name?.toLowerCase() || '').includes(q)
        );
      })
    : [];

  const organiseMark = async (studentlist) => {
    setNotify(false);
    const server_response = await ajaxMark.organiseMarks(studentlist, classRoom.class_id, paper.value);
    if (server_response.status === "OK") {
      getOrganisedMarks();
      setStudentProgess(false);
    } else {
      setNotify(<Notification type="info" message={server_response.message} />);
    }
  };

  const addMark = (studentId) => {
    setStudentProgess(studentId);
    organiseMark([studentId]);
  };

  const onClickRemove = (record) => {
    setWarnDelete(record);
  };

  const handleAddAll = () => {
    if (!classRoom || !paper) {
      setNotify(<Notification type="warning" message="Please select a class and subject paper first." />);
      return;
    }
    setShowOrganiseClass(true);
  }

  const handleRemoveAll = () => {
    if (!classRoom || !paper) {
      setNotify(<Notification type="warning" message="Please select a class and subject paper first." />);
      return;
    }
    setShowUnorganiseClass(true);
  }

  const handleRecallAdd = () => {
    if (!classRoom) {
      setNotify(<Notification type="warning" message="Please select a class first." />);
      return;
    }
    setShowRecallAdd(true);
  }

  const handleCopyMarks = () => {
    openSettings('Copy Marks', 'CopyMarks')
  }

  const PageActions = () => (
        <>
            <ActionDropdownItem iconClass="fa fa-plus text-primary" label="Add All" onClick={handleAddAll}/>
            <ActionDropdownItem iconClass="fa fa-trash-alt text-danger" label="Remove All" onClick={handleRemoveAll}/>
            <ActionDropdownItem iconClass="fa fa-sync text-primary" label="Recall & Add" onClick={handleRecallAdd}/>
            <ActionDropdownItem iconClass="fa fa-copy text-primary" label="Copy Marks" onClick={handleCopyMarks}/>

        </>
    );


  return (
    <>
      <PageContainer>
        <MainContent>
          <PageTitle
            title="Organise Marksheets"
            description="Create and manage marksheets for current term: Select class/stream and subject."
            iconClass="fa fa-book"
            PageActions={PageActions}
            sticky
          />

          <div className="text-end mb-2">
            <button
              className="btn btn-sm btn-light border"
              onClick={() => dispatch({ type: 'TOGGLE_FILTER' })}
            >
              <i className={`fa ${state.showFilter ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i> Filters
            </button>
          </div>

          {!state.showFilter && Array.isArray(organisedMarks) && (
            <div className="alert alert-secondary d-flex align-items-center justify-content-between">
              <div className="fw-semibold">
                <i className="fa fa-bullseye me-2 text-primary"></i>&nbsp;
                Currently organising: <strong>{classLabel} {streamLabel && ` ${streamLabel}`} - {subjectLabel}</strong> (Term {currentAcademicPeriod?.term.term} {currentAcademicPeriod?.acad_year})
              </div>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => dispatch({ type: 'TOGGLE_FILTER' })}
              >
                Change
              </button>
            </div>
          )}

          <div className={`slide-toggle ${state.showFilter ? 'open' : 'closed'} mb-3`}>
            <div className="card shadow-sm border-0">
              <div className="card-header bg-light d-flex align-items-center">
                <i className="fa fa-filter text-primary me-2"></i>
                <h6 className="mb-0">FILTER BY CLASS, STREAM & SUBJECT</h6>
              </div>
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-md-4">
                    <TextInput
                      label="Class"
                      placeholder={classRoom ? classRoom.class_name : 'Select class'}
                      type="select"
                      options={classOptions}
                      onChange={(item) => {
                        setClass(item.value);
                      }}
                      wrap
                      required
                    />
                  </div>
                  {streamOptions.length > 0 && (
                    <div className="col-md-4">
                      <TextInput
                        label="Stream"
                        placeholder={stream ? stream.label : 'Select stream'}
                        type="select"
                        options={streamOptions}
                        onChange={(item) => {
                          setStream(item);
                        }}
                        wrap
                        required
                      />
                    </div>
                  )}
                  <div className="col-md-4">
                    <TextInput
                      label="Subject Paper"
                      placeholder={paper ? paper.label : 'Select subject paper'}
                      type="select"
                      options={subjectOptions}
                      onChange={(item) => {
                        setPaper(item);
                      }}
                      wrap
                      required
                    />
                  </div>
                </div>
                <div className="text-end">
                  <button className="btn btn-primary shadow-sm" onClick={handleLoadStudents}>
                    <i className="fa fa-users"></i> Load Students
                  </button>
                </div>
              </div>
            </div>
          </div>

          {!Array.isArray(organisedMarks) && !loadingOrganizer && <Notification message="Please select class and subject to begin." />}

          {(Array.isArray(organisedMarks)) && (
            <>
              <div className="mb-3">
                <div className="input-group input-group-lg bg-white">
                  <span className="input-group-text bg-white border-end-0">
                    <i className="fa fa-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0 rounded-end search-input"
                    placeholder="Search by name or reg. no"
                    value={state.searchQuery}
                    onChange={(e) => dispatch({ type: 'SET_SEARCH_QUERY', value: e.target.value })}
                  />
                </div>
              </div>

              <div className="table-responsive card mb-3">
                <table className="table table-striped table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>No.</th>
                      <th>Reg. No</th>
                      <th>Student Name</th>
                      <th>Class</th>
                      <th style={{ width: '140px' }}>&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((record, index) => (
                      <tr key={record.student.id}>
                        <td>{index + 1}</td>
                        <td>{record.student.reg_no}</td>
                        <td>{record.student.full_name}</td>
                        <td>{record.student.class.class_name}</td>
                        <td>
                          {(record.student.id === studentProgress) && <ActivityLoader size={20} inline />}
                          {!(record.student.id === studentProgress) && <>
                            {!record.mark && <button className='btn btn-outline-primary btn-sm' onClick={() => addMark(record.student.id)}>Add Student</button>}
                            {record.mark && <button className='btn btn-outline-danger btn-sm' onClick={() => onClickRemove(record)}>Remove</button>}
                          </>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {loadingOrganizer && <ActivityLoader />}

        </MainContent>
      </PageContainer>
      {warnDelete && <AlertDeleteMark record={warnDelete} handleClose={() => setWarnDelete(false)} />}
      <AlertOrganiseClass show={showOrganiseClass} handleClose={()=>setShowOrganiseClass(false)} />
      <AlertUnorganiseClass show={showUnorganiseClass} handleClose={()=>setShowUnorganiseClass(false)} />
      <AlertRecallAdd show={showRecallAdd} handleClose={()=>setShowRecallAdd(false)} />
      <AlertCopyMarks />
    </>
  );
};

export default OrganiseMarksPage;
